import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'src/app/service/message.service';
import { ProductService } from 'src/app/service/product.service';
import { groupThemes, Theme, ThemeGroupSection } from 'src/app/model/theme';

/**
 * 상품 테마 선택 칩. 어드민 목록 모달 / 상품 등록 / 상품 수정 세 곳이 함께 쓴다.
 *
 * 17건 고정 taxonomy라 드롭다운으로 감추지 않고 그룹별로 전부 펼쳐 보여준다.
 * 그룹별 선택 상한(`theme_group.max_selectable`)은 칩 단계에서 막는다 — 저장 후 400을 받고
 * 고치는 왕복을 없애기 위함이고, 서버 검증은 그대로 살아 있다(목록이 낡은 경우의 방어).
 *
 * 값은 **id 배열**로 주고받는다. 이름이 아니라 id 라서 테마명이 겹치거나 바뀌어도 매핑이 어긋나지 않는다.
 */
@Component({
  selector: 'theme-picker',
  templateUrl: './theme-picker.component.html',
  styleUrls: ['./theme-picker.component.scss'],
})
export class ThemePickerComponent implements OnInit, OnChanges {
  /** 선택된 테마 id. `[(selectedIds)]` 로 양방향 바인딩한다. */
  @Input() selectedIds: number[] = [];
  @Output() selectedIdsChange = new EventEmitter<number[]>();

  sections: ThemeGroupSection[] = [];
  /** 조회 실패와 '테마가 아직 없음'을 구분하기 위한 플래그. */
  loadFailed = false;

  private selected = new Set<number>();

  constructor(
    private productService: ProductService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.productService.searchTheme().subscribe({
      next: (themes) => (this.sections = groupThemes(themes)),
      // 조용히 빈 목록으로 두면 '테마가 아직 없음'과 구분되지 않는다.
      error: (err) => {
        this.loadFailed = true;
        this.messageService.toast('테마 목록 조회 실패: ' + (err.error?.message || err.message));
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 부모가 모달을 열 때마다 현재 값을 새로 내려주므로 그때마다 내부 Set 을 맞춘다.
    if (changes.selectedIds) {
      this.selected = new Set(this.selectedIds ?? []);
    }
  }

  isSelected(theme: Theme): boolean {
    return this.selected.has(theme.id);
  }

  selectedCountIn(section: ThemeGroupSection): number {
    return section.themes.filter((theme) => this.selected.has(theme.id)).length;
  }

  /** 상한이 있는 그룹인데 이미 다 채웠는지. 헤더의 개수 표시를 강조하는 데 쓴다. */
  isGroupAtCap(section: ThemeGroupSection): boolean {
    const max = section.group.maxSelectable;
    return max !== null && this.selectedCountIn(section) >= max;
  }

  /** 이미 선택된 칩은 해제할 수 있어야 하므로 막지 않는다. */
  isChipDisabled(section: ThemeGroupSection, theme: Theme): boolean {
    return !this.selected.has(theme.id) && this.isGroupAtCap(section);
  }

  toggle(section: ThemeGroupSection, theme: Theme): void {
    if (this.selected.has(theme.id)) {
      this.selected.delete(theme.id);
    } else {
      if (this.isChipDisabled(section, theme)) return;
      this.selected.add(theme.id);
    }
    this.selectedIdsChange.emit([...this.selected]);
  }
}
